/**
 * Sponsor Performance Components
 * Modular analytics blocks for sponsor intelligence dashboard
 * Moat feature for CamPassed
 */

import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  MousePointer,
  Target,
  Users,
  Building2,
  Calendar,
  MapPin,
  BarChart3,
  Loader2,
  AlertCircle,
  Sparkles,
  ExternalLink,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Glass panel component
const GlassPanel = ({ children, className = '', ...props }) => (
  <div
    className={cn(
      'bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-2xl',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

// Trend indicator
const TrendIndicator = ({ direction, percentage, className = '' }) => {
  const config = {
    up: { icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    down: { icon: TrendingDown, color: 'text-rose-400', bg: 'bg-rose-500/10' },
    neutral: { icon: Minus, color: 'text-white/40', bg: 'bg-white/[0.05]' },
  };

  const { icon: Icon, color, bg } = config[direction] || config.neutral;

  return (
    <div className={cn('flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium', bg, color, className)}>
      <Icon className="w-3 h-3" />
      {percentage > 0 && `${percentage}%`}
    </div>
  );
};

// ==========================================
// METRIC CARD - Single metric display
// ==========================================
export const MetricCard = ({
  label,
  value,
  trend,
  icon: Icon = BarChart3,
  color = 'text-cyan-400',
  prefix = '',
  suffix = '',
  tooltip,
}) => (
  <GlassPanel className="p-5" data-testid="metric-card">
    <div className="flex items-start justify-between">
      <div>
        <div className="flex items-center gap-2">
          <p className="text-white/50 text-sm">{label}</p>
          {tooltip && (
            <div className="relative group">
              <Info className="w-3.5 h-3.5 text-white/30 cursor-help" />
              <div className="absolute left-0 bottom-full mb-2 w-48 p-2 bg-[#1a1a2e] border border-white/[0.1] rounded-lg text-xs text-white/60 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                {tooltip}
              </div>
            </div>
          )}
        </div>
        <p className={cn('text-2xl font-mono font-bold mt-1', color)}>
          {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
        </p>
        {trend && (
          <TrendIndicator
            direction={trend.direction}
            percentage={trend.percentage}
            className="mt-2"
          />
        )}
      </div>
      <div className={cn('p-3 rounded-xl', `bg-${color.replace('text-', '')}/10`)}>
        <Icon className={cn('w-6 h-6', color)} />
      </div>
    </div>
  </GlassPanel>
);

// ==========================================
// PERFORMANCE SUMMARY BLOCK
// ==========================================
export const PerformanceSummary = ({ data, loading = false }) => {
  if (loading) {
    return (
      <GlassPanel className="p-8">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          <span className="text-white/50">Loading performance data...</span>
        </div>
      </GlassPanel>
    );
  }

  if (!data?.metrics) {
    return (
      <GlassPanel className="p-8 text-center">
        <BarChart3 className="w-12 h-12 text-white/20 mx-auto mb-3" />
        <p className="text-white/50">No performance data available</p>
      </GlassPanel>
    );
  }

  const { metrics, insights } = data;

  return (
    <div className="space-y-4" data-testid="performance-summary">
      {/* Metric Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Impressions"
          value={metrics.impressions?.total || 0}
          trend={metrics.impressions?.trend}
          icon={Eye}
          color="text-violet-400"
          tooltip="Total times your ads were displayed"
        />
        <MetricCard
          label="Clicks"
          value={metrics.clicks?.total || 0}
          trend={metrics.clicks?.trend}
          icon={MousePointer}
          color="text-cyan-400"
          tooltip="Total clicks on your ads"
        />
        <MetricCard
          label="CTR"
          value={metrics.ctr?.value || 0}
          icon={Target}
          color="text-emerald-400"
          suffix="%"
          tooltip={`Click-through rate. Industry avg: ${metrics.ctr?.benchmark || 2.5}%`}
        />
        <MetricCard
          label="Reach"
          value={metrics.reach?.unique_users || 0}
          icon={Users}
          color="text-amber-400"
          tooltip="Unique users who saw your ads"
        />
      </div>

      {/* Insights */}
      {insights && insights.length > 0 && (
        <GlassPanel className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-white">Insights</span>
          </div>
          <div className="space-y-2">
            {insights.map((insight, i) => {
              const colorMap = {
                positive: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
                warning: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
                info: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
              };
              return (
                <div
                  key={i}
                  className={cn(
                    'px-3 py-2 rounded-lg text-sm border',
                    colorMap[insight.type] || colorMap.info
                  )}
                >
                  {insight.message}
                </div>
              );
            })}
          </div>
        </GlassPanel>
      )}
    </div>
  );
};

// ==========================================
// CAMPUS METRICS BLOCK
// ==========================================
export const CampusMetrics = ({ data, loading = false, onViewAll }) => {
  if (loading) {
    return (
      <GlassPanel className="p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
          <span className="text-white/50">Loading campus data...</span>
        </div>
      </GlassPanel>
    );
  }

  const campuses = data?.campuses || [];

  return (
    <GlassPanel className="p-5" data-testid="campus-metrics">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-violet-400" />
          <h3 className="font-medium text-white">Campus Performance</h3>
        </div>
        {onViewAll && campuses.length > 0 && (
          <button onClick={onViewAll} className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
            View All <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>

      {campuses.length === 0 ? (
        <div className="text-center py-6">
          <MapPin className="w-10 h-10 text-white/20 mx-auto mb-2" />
          <p className="text-white/40 text-sm">No campus data yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campuses.slice(0, 5).map((campus, i) => (
            <div
              key={campus.campus_id || i}
              className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl hover:bg-white/[0.04] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                  <span className="text-xs font-mono font-bold text-violet-400">#{i + 1}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{campus.campus_name}</p>
                  {campus.location && (
                    <p className="text-xs text-white/40">{campus.location}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-mono text-white">{campus.impressions?.toLocaleString()}</p>
                <p className="text-xs text-white/40">{campus.ctr}% CTR</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Aggregates */}
      {data?.aggregates && (
        <div className="mt-4 pt-4 border-t border-white/[0.06] grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-lg font-mono font-bold text-white">{data.total_campuses}</p>
            <p className="text-xs text-white/40">Campuses</p>
          </div>
          <div>
            <p className="text-lg font-mono font-bold text-white">{data.aggregates.total_impressions?.toLocaleString()}</p>
            <p className="text-xs text-white/40">Impressions</p>
          </div>
          <div>
            <p className="text-lg font-mono font-bold text-white">{data.aggregates.avg_ctr}%</p>
            <p className="text-xs text-white/40">Avg CTR</p>
          </div>
        </div>
      )}
    </GlassPanel>
  );
};

// ==========================================
// ORGANIZATION METRICS BLOCK
// ==========================================
export const OrganizationMetrics = ({ data, loading = false, onViewAll }) => {
  if (loading) {
    return (
      <GlassPanel className="p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
          <span className="text-white/50">Loading organization data...</span>
        </div>
      </GlassPanel>
    );
  }

  const organizations = data?.organizations || [];

  return (
    <GlassPanel className="p-5" data-testid="organization-metrics">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-cyan-400" />
          <h3 className="font-medium text-white">Organization Performance</h3>
        </div>
        {onViewAll && organizations.length > 0 && (
          <button onClick={onViewAll} className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
            View All <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>

      {organizations.length === 0 ? (
        <div className="text-center py-6">
          <Building2 className="w-10 h-10 text-white/20 mx-auto mb-2" />
          <p className="text-white/40 text-sm">No organization data yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {organizations.slice(0, 5).map((org, i) => (
            <div
              key={org.organization_id || i}
              className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl hover:bg-white/[0.04] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <span className="text-xs font-mono font-bold text-cyan-400">#{i + 1}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{org.organization_name}</p>
                  <p className="text-xs text-white/40">@{org.slug}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-mono text-white">{org.impressions?.toLocaleString()}</p>
                <p className="text-xs text-white/40">{org.ctr}% CTR</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Aggregates */}
      {data?.aggregates && (
        <div className="mt-4 pt-4 border-t border-white/[0.06] grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-lg font-mono font-bold text-white">{data.total_organizations}</p>
            <p className="text-xs text-white/40">Organizations</p>
          </div>
          <div>
            <p className="text-lg font-mono font-bold text-white">{data.aggregates.total_clicks?.toLocaleString()}</p>
            <p className="text-xs text-white/40">Clicks</p>
          </div>
          <div>
            <p className="text-lg font-mono font-bold text-white">{data.aggregates.avg_ctr}%</p>
            <p className="text-xs text-white/40">Avg CTR</p>
          </div>
        </div>
      )}
    </GlassPanel>
  );
};

// ==========================================
// EVENT METRICS BLOCK
// ==========================================
export const EventMetrics = ({ data, loading = false, onViewAll }) => {
  if (loading) {
    return (
      <GlassPanel className="p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
          <span className="text-white/50">Loading event data...</span>
        </div>
      </GlassPanel>
    );
  }

  const events = data?.events || [];

  return (
    <GlassPanel className="p-5" data-testid="event-metrics">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-400" />
          <h3 className="font-medium text-white">Event Performance</h3>
        </div>
        {onViewAll && events.length > 0 && (
          <button onClick={onViewAll} className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
            View All <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>

      {events.length === 0 ? (
        <div className="text-center py-6">
          <Calendar className="w-10 h-10 text-white/20 mx-auto mb-2" />
          <p className="text-white/40 text-sm">No event data yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.slice(0, 5).map((event, i) => (
            <div
              key={event.event_id || i}
              className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl hover:bg-white/[0.04] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <span className="text-xs font-mono font-bold text-emerald-400">#{i + 1}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-white truncate max-w-[200px]">{event.event_name}</p>
                  {event.start_time && (
                    <p className="text-xs text-white/40">
                      {new Date(event.start_time).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-mono text-white">{event.impressions?.toLocaleString()}</p>
                <p className="text-xs text-white/40">{event.ctr}% CTR</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Aggregates */}
      {data?.aggregates && (
        <div className="mt-4 pt-4 border-t border-white/[0.06] grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-lg font-mono font-bold text-white">{data.total_events}</p>
            <p className="text-xs text-white/40">Events</p>
          </div>
          <div>
            <p className="text-lg font-mono font-bold text-white">{data.aggregates.total_impressions?.toLocaleString()}</p>
            <p className="text-xs text-white/40">Impressions</p>
          </div>
          <div>
            <p className="text-lg font-mono font-bold text-white">{data.aggregates.avg_ctr}%</p>
            <p className="text-xs text-white/40">Avg CTR</p>
          </div>
        </div>
      )}
    </GlassPanel>
  );
};

// ==========================================
// CONVERSION PLACEHOLDER BLOCK
// ==========================================
export const ConversionMetrics = ({ data, loading = false }) => {
  return (
    <GlassPanel className="p-5 relative overflow-hidden" data-testid="conversion-metrics">
      {/* Coming Soon Overlay */}
      <div className="absolute inset-0 bg-[#0a0a14]/80 backdrop-blur-sm flex flex-col items-center justify-center z-10">
        <Target className="w-10 h-10 text-violet-400 mb-3" />
        <p className="text-white font-medium">Conversion Tracking</p>
        <p className="text-white/40 text-sm mt-1">Coming Soon</p>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Target className="w-5 h-5 text-violet-400" />
        <h3 className="font-medium text-white">Conversions</h3>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-4 bg-white/[0.02] rounded-xl">
          <p className="text-2xl font-mono font-bold text-white">0</p>
          <p className="text-xs text-white/40 mt-1">Total</p>
        </div>
        <div className="text-center p-4 bg-white/[0.02] rounded-xl">
          <p className="text-2xl font-mono font-bold text-white">0%</p>
          <p className="text-xs text-white/40 mt-1">Rate</p>
        </div>
        <div className="text-center p-4 bg-white/[0.02] rounded-xl">
          <p className="text-2xl font-mono font-bold text-white">$0</p>
          <p className="text-xs text-white/40 mt-1">Value</p>
        </div>
      </div>
    </GlassPanel>
  );
};

// ==========================================
// SPONSOR ANALYTICS DASHBOARD HOOK
// ==========================================
export const useSponsorAnalytics = (sponsorId, session, period = 'last_30_days') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!sponsorId || !session?.access_token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await fetch(
          `${API_URL}/api/sponsor-analytics/sponsors/${sponsorId}/dashboard?period=${period}`,
          {
            headers: { Authorization: `Bearer ${session.access_token}` },
          }
        );

        if (res.ok) {
          const result = await res.json();
          setData(result);
        } else {
          setError('Failed to fetch analytics');
        }
      } catch (err) {
        console.error('Error fetching sponsor analytics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [sponsorId, session?.access_token, period, API_URL]);

  return { data, loading, error };
};

export default {
  MetricCard,
  PerformanceSummary,
  CampusMetrics,
  OrganizationMetrics,
  EventMetrics,
  ConversionMetrics,
  useSponsorAnalytics,
};
